import React, { useState } from 'react';
import { SideBarModal, Button, showErrorToast } from '@/components';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { LabelLayout, PackageRow } from '@/services/api/packages';
import { ShipmentDetail, shipmentToPackageRows } from '@/services/api/shipments';
import { useAuth } from '@/contexts/AuthContext';
import { LabelPreview } from '@/components/LabelPreviewModal/LabelPreview';
import {
  downloadLabelPdf,
  printLabelPdf,
} from '@/components/LabelPreviewModal/labelPrinter';

interface BulkPrintShipmentsModalProps {
  open: boolean;
  // Shipments (with items) resolved from getBulkShipmentItems — the review
  // list shows what will be printed before the user commits.
  shipments: ShipmentDetail[];
  onClose: () => void;
}

const LAYOUTS: { key: LabelLayout; label: string }[] = [
  { key: 'THERMAL_4X6', label: 'Thermal full' },
  { key: 'THERMAL_100X100', label: 'Thermal 100×100' },
  { key: 'A4_2UP', label: 'A4 (1 / page)' },
];

// Only non-cancelled items get a label (matches shipmentToPackageRows).
const printableItems = (s: ShipmentDetail) =>
  (s.items ?? []).filter((it) => it.status !== 'CANCELLED');

// Labeled field — a small uppercase caption above the value so each datum is
// self-describing (not just a bare name).
const Field: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="min-w-0">
    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-soft-400">
      {label}
    </div>
    {/* Never truncate — booking IDs / receiver names must show in full, so wrap
        instead of clipping. `break-all` lets long mono codes break anywhere. */}
    <div
      className={`mt-0.5 font-medium text-text-strong-950 ${
        mono ? 'break-all font-mono text-xs' : 'break-words text-sm'
      }`}
    >
      {value}
    </div>
  </div>
);

const BulkPrintShipmentsModal: React.FC<BulkPrintShipmentsModalProps> = ({
  open,
  shipments,
  onClose,
}) => {
  const { client } = useAuth();
  const [layout, setLayout] = useState<LabelLayout>('THERMAL_4X6');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Rows drive both the preview and the PDF; shipmentToPackageRows already
  // drops cancelled items, so `rows.length` is the true number of labels.
  // Pass the authed client as the header fallback (Link is single-client).
  const rows: PackageRow[] = shipments.flatMap((s) =>
    shipmentToPackageRows(s, client)
  );
  const totalLabels = rows.length;
  const totalWeight = shipments
    .flatMap(printableItems)
    .reduce((sum, it) => sum + (it.weight ?? 0), 0);
  const busy = isDownloading || isPrinting;

  const handleDownload = async () => {
    if (rows.length === 0) return;
    setIsDownloading(true);
    try {
      await downloadLabelPdf(
        rows,
        layout,
        `labels-${shipments.length}-shipment.pdf`
      );
    } catch (err) {
      showErrorToast('Gagal mengunduh PDF', getBackendErrorMessage(err));
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (rows.length === 0) return;
    setIsPrinting(true);
    try {
      await printLabelPdf(rows, layout);
    } catch (err) {
      showErrorToast('Gagal mencetak label', getBackendErrorMessage(err));
    } finally {
      setIsPrinting(false);
    }
  };

  const header = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
        <i className="ri-printer-line text-lg text-primary" />
      </div>
      <div>
        <h2 className="text-base font-semibold tracking-tight text-text-strong-950">
          Print labels
        </h2>
        <p className="text-xs text-text-sub-600">
          {shipments.length} shipment · {totalLabels} label · 1 label / halaman
        </p>
      </div>
    </div>
  );

  const footer = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Layout picker — updates the live preview */}
      <div className="inline-flex gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
        {LAYOUTS.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => setLayout(l.key)}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              layout === l.key
                ? 'bg-bg-white-0 text-text-strong-950 shadow-card-xs'
                : 'text-text-sub-600 hover:text-text-strong-950'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="white" onClick={onClose} disabled={busy}>
          Batal
        </Button>
        <Button
          variant="white"
          onClick={handleDownload}
          disabled={busy || totalLabels === 0}
        >
          <i
            className={`mr-1.5 ${isDownloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'}`}
          />
          Download PDF
        </Button>
        <Button onClick={handlePrint} disabled={busy || totalLabels === 0}>
          <i
            className={`mr-1.5 ${isPrinting ? 'ri-loader-4-line animate-spin' : 'ri-printer-line'}`}
          />
          Print
        </Button>
      </div>
    </div>
  );

  return (
    <SideBarModal
      isOpen={open}
      onClose={onClose}
      header={header}
      footer={footer}
      width="xl"
      closeOnBackdropClick={!busy}
    >
      <div className="flex flex-col gap-5 p-5 lg:flex-row lg:items-start">
        {/* ── Left: per-shipment review ─────────────────────────────── */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* Overview strip */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-bg-weak-50 px-3 py-2 text-xs text-text-sub-600">
            <span>
              <b className="text-text-strong-950 tabular-nums">
                {shipments.length}
              </b>{' '}
              shipment
            </span>
            <span className="h-3 w-px bg-stroke-soft-200" />
            <span>
              <b className="text-text-strong-950 tabular-nums">{totalLabels}</b>{' '}
              label
            </span>
            <span className="h-3 w-px bg-stroke-soft-200" />
            <span>
              <b className="text-text-strong-950 tabular-nums">
                {totalWeight.toFixed(1)}
              </b>{' '}
              kg
            </span>
          </div>

          {shipments.map((s, idx) => {
            const items = printableItems(s);
            return (
              <div
                key={s.id}
                className="overflow-hidden rounded-xl border-2 border-stroke-sub-300 bg-bg-white-0 shadow-card-xs"
              >
                {/* Shipment header — numbered + labeled fields on a tinted band
                    so each shipment reads as a clearly separate block. */}
                <div className="flex items-start justify-between gap-3 border-b-2 border-stroke-sub-300 bg-bg-weak-50 px-4 py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white tabular-nums">
                      {idx + 1}
                    </span>
                    <div className="min-w-0 space-y-2">
                      <Field label="Waybill" value={s.waybill} mono />
                      <Field
                        label="Booking ID"
                        value={s.bookingId || '—'}
                        mono
                      />
                      <Field label="Receiver" value={s.receiverName || '—'} />
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary tabular-nums">
                    {items.length} label
                  </span>
                </div>

                {/* Items that will be printed */}
                <div className="px-4 py-3">
                  <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-soft-400">
                    Items ({items.length})
                  </div>
                  {items.length === 0 ? (
                    <p className="text-xs italic text-text-soft-400">
                      Tidak ada item untuk dicetak.
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {items.map((it, i) => (
                        <li
                          key={it.id ?? `${s.id}-${i}`}
                          className="flex items-center justify-between gap-3 rounded-lg bg-bg-weak-50 px-3 py-2"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <i className="ri-price-tag-3-line shrink-0 text-sm text-text-soft-400" />
                            <span className="min-w-0 truncate font-mono text-xs text-text-strong-950">
                              {it.itemName || it.invoice || `Item ${i + 1}`}
                            </span>
                          </span>
                          <span className="shrink-0 text-[11px] text-text-sub-600 tabular-nums">
                            {it.quantity ?? 1}× · {it.weight ?? 0} kg
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Right: live label preview ─────────────────────────────── */}
        <div className="lg:w-[340px] lg:shrink-0">
          <div className="lg:sticky lg:top-0">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
              Preview label
            </div>
            <div className="flex justify-center overflow-auto rounded-xl border border-stroke-soft-200 bg-bg-weak-50 p-4">
              {rows[0] ? (
                <LabelPreview pkg={rows[0]} layout={layout} />
              ) : (
                <p className="py-10 text-xs text-text-soft-400">
                  Tidak ada label untuk dipratinjau.
                </p>
              )}
            </div>
            {totalLabels > 1 && (
              <p className="mt-2 text-center text-[11px] text-text-soft-400">
                +{totalLabels - 1} label lagi · PDF multi-halaman
              </p>
            )}
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-bg-weak-50 px-3 py-2.5 text-[11px] text-text-sub-600">
              <i className="ri-qr-code-line mt-0.5 shrink-0 text-primary" />
              <span>
                QR hanya untuk scan internal &amp; driver app. 1 item = 1 label.
              </span>
            </div>
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};

export default BulkPrintShipmentsModal;
